import { z } from "zod";

// ============================================================================
// COST SCHEMAS
// ============================================================================

export const TrendsCategorySchema = z.enum([
  "total",
  "clusters",
  "sql_warehouse",
  "ai_ml",
  "lakehouse",
  "platform",
]);

export type TrendsCategory = z.infer<typeof TrendsCategorySchema>;

export const CostCategoryItemSchema = z.object({
  category: TrendsCategorySchema,
  label: z.string(),
  total_dbus: z.number(),
  estimated_cost: z.number(),
  pct_of_total: z.number(),
});

export type CostCategoryItem = z.infer<typeof CostCategoryItemSchema>;

export const SummaryCostOverviewResponseSchema = z.object({
  total_dbus: z.number(),
  estimated_cost: z.number(),
  days_count: z.number(),
  workspace_count: z.number(),
  sku_count: z.number(),
  delta_pct: z.number().nullable().optional(),
  daily_average_dbus: z.number(),
  // Drop items whose `category` is outside the enum so a single unknown
  // backend value doesn't invalidate the whole array.
  category_breakdown: z
    .preprocess(
      val =>
        Array.isArray(val)
          ? val.filter(
              item =>
                TrendsCategorySchema.safeParse((item as { category?: unknown })?.category).success
            )
          : val,
      z.array(CostCategoryItemSchema)
    )
    .default([]),
});

export type SummaryCostOverviewResponse = z.infer<typeof SummaryCostOverviewResponseSchema>;

export const SummaryProductTrendItemSchema = z.object({
  period: z.string(),
  product: z.string(),
  total_dbus: z.number(),
  estimated_cost: z.number(),
});

export const SummaryProductTrendsResponseSchema = z.object({
  trends: z.array(SummaryProductTrendItemSchema),
  products: z.array(z.string()),
  granularity: z.string(),
  category: TrendsCategorySchema,
});

export type SummaryProductTrendItem = z.infer<typeof SummaryProductTrendItemSchema>;
export type SummaryProductTrendsResponse = z.infer<typeof SummaryProductTrendsResponseSchema>;

// Cost estimate schemas for Future State projections.
// The estimate endpoints slice by `category` exactly like /cost/trends/by-product:
// each segment's `product` is a spec-group label (category=total) or a raw
// product/tier name (category=<group>).
export const EstimateSegmentItemSchema = z.object({
  product: z.string(),
  cost: z.number().default(0),
  cost_estimate: z.number().default(0),
  percentage_change: z.number().nullable().optional(),
});

export type EstimateSegmentItem = z.infer<typeof EstimateSegmentItemSchema>;

export const SummaryCostEstimateGraphItemSchema = z.object({
  date: z.string(),
  total: z.number().default(0),
  total_estimate: z.number().default(0),
  is_estimate: z.boolean().default(false),
  percentage_total: z.number().nullable().optional(),
  segments: z.array(EstimateSegmentItemSchema).default([]),
});

export const SummaryCostEstimateTrendsResponseSchema = z.object({
  graph: z.array(SummaryCostEstimateGraphItemSchema),
  products: z.array(z.string()).default([]),
  category: TrendsCategorySchema,
  currency: z.string().default("USD"),
});

export type SummaryCostEstimateGraphItem = z.infer<typeof SummaryCostEstimateGraphItemSchema>;
export type SummaryCostEstimateTrendsResponse = z.infer<
  typeof SummaryCostEstimateTrendsResponseSchema
>;

export const EstimateSegmentTotalItemSchema = z.object({
  product: z.string(),
  estimated_cost: z.number(),
  pct_of_total: z.number(),
});

export type EstimateSegmentTotalItem = z.infer<typeof EstimateSegmentTotalItemSchema>;

export const SummaryCostTotalEstimateResponseSchema = z.object({
  total: z.number(),
  currency: z.string().default("USD"),
  category: TrendsCategorySchema,
  segment_breakdown: z.array(EstimateSegmentTotalItemSchema).default([]),
});

export type SummaryCostTotalEstimateResponse = z.infer<
  typeof SummaryCostTotalEstimateResponseSchema
>;

// ============================================================================
// SAVINGS SCHEMAS
// ============================================================================

export const SummarySavingsOverallResponseSchema = z.object({
  total_savings: z.number(),
  total_autonomous_savings: z.number().default(0),
  total_assisted_savings: z.number().default(0),
  min_total_autonomous_savings: z.number().default(0),
  max_total_autonomous_savings: z.number().default(0),
  time_saved: z.string().default("0 days"),
  currency: z.string().default("USD"),
});

export type SummarySavingsOverallResponse = z.infer<typeof SummarySavingsOverallResponseSchema>;

export const SummarySavingsOverallEstimateResponseSchema = z.object({
  total_savings: z.number(),
  total_autonomous_savings: z.number().default(0),
  total_assisted_savings: z.number().default(0),
  min_total_autonomous_savings: z.number().default(0),
  max_total_autonomous_savings: z.number().default(0),
  time_saved: z.string().default("0 days"),
  total_savings_estimate: z.number().default(0),
  total_autonomous_savings_estimate: z.number().default(0),
  total_assisted_savings_estimate: z.number().default(0),
  total_time_saved_estimate: z.string().default("0 days"),
  currency: z.string().default("USD"),
});

export type SummarySavingsOverallEstimateResponse = z.infer<
  typeof SummarySavingsOverallEstimateResponseSchema
>;

// ============================================================================
// API PARAMETER TYPES
// ============================================================================

export interface SummaryCostParams {
  start_date: string;
  end_date: string;
}

export interface SummaryTrendsParams extends SummaryCostParams {
  granularity?: "day" | "week" | "month";
  category?: TrendsCategory;
}

export interface SummarySavingsParams {
  start_date: string;
  end_date: string;
  savings_period?: string;
}

export interface SavingsData {
  total: number;
  autonomous: number;
  assisted: number;
  timeSaved: string;
}
